import React, { useEffect, useMemo, useState } from 'react';
import Swiper from 'react-id-swiper';
import SwiperCore, { Navigation } from 'swiper';
import { imageUrl, relativeTime, zeroStringFormat } from 'utils/utils';
import { all_click_datalayer_push, getContentsGAProps, getEventGAProps } from 'utils/GAUtils';
import { MESSAGE_EVENT_IDS } from '../../../Event/constants';
import { GET_LOADED_ENVIRONMENT_TYPE } from '../../../../../commons/constants';
import Paths from '../../../../../commons/paths';
import useExecutiveAppointment from '../../../../../hooks/useExecutiveAppointment';
import { useHistory } from 'react-router-dom';
import { SwiperSlide } from 'swiper/react';
import _ from 'lodash';
import moment from 'moment';

SwiperCore.use([Navigation]);

interface HeroProps extends MainHeroListResDto {
  currentDt: string;
}

/** 내년 신년사 컨텐츠 id 목록 (고정) */
const PINNED_NEWYEAR_CONTENTS_ID_LIST = [
  {
    environmentType: 'localhost',
    mediaId: '712',
  },
  {
    environmentType: 'h9development',
    mediaId: '712',
  },
  {
    environmentType: 'lgdevelopment',
    mediaId: 'MED000000000000000000000210',
  },
  {
    environmentType: 'production',
    mediaId: 'MED000000000000000000002682',
  },
];

/** 고정 노출할 LG 임원 인사 HOME HERO ID 목록 */
const PINNED_EXECUTIVE_APPOINTMENT_HOME_HERO_ID_LIST = [
  /** HOME 1번 슬롯 */
  {
    slowNum: 1,
    targetHeroList: [
      {
        environmentType: 'localhost',
        heroId: 'HER000000000000000000002495',
      },
      {
        environmentType: 'h9development',
        heroId: 'HER000000000000000000002495',
      },
      {
        environmentType: 'lgdevelopment',
        heroId: 'HER000000000000000000002495',
      },
      {
        environmentType: 'production',
        heroId: 'HER000000000000000000008075',
      },
    ],
  },
  /** HOME 2번 슬롯 */
  {
    slowNum: 2,
    targetHeroList: [
      {
        environmentType: 'localhost',
        heroId: 'HER000000000000000000000302',
      },
      {
        environmentType: 'h9development',
        heroId: 'HER000000000000000000000302',
      },
      {
        environmentType: 'lgdevelopment',
        heroId: 'HER000000000000000000000360',
      },
      {
        environmentType: 'production',
        heroId: 'HER000000000000000000002832',
      },
    ],
  },
  /** HOME 3번 슬롯 */
  {
    slowNum: 3,
    targetHeroList: [
      {
        environmentType: 'localhost',
        heroId: 'HER000000000000000000000614',
      },
      {
        environmentType: 'h9development',
        heroId: 'HER000000000000000000000614',
      },
      {
        environmentType: 'lgdevelopment',
        heroId: 'HER000000000000000000000359',
      },
      {
        environmentType: 'production',
        heroId: 'HER000000000000000000002833',
      },
    ],
  },
];

const Hero = (props: HeroProps) => {
  const { mainHeroList } = props;
  const history = useHistory();
  const { getExecutiveAppointmentYearDisplayInfo } = useExecutiveAppointment();
  const { data: executeAppointmentDisplayInfo } = getExecutiveAppointmentYearDisplayInfo();

  const [screenSize, setScreenSize] = useState('');

  const handleResize = () => {
    const width = window.innerWidth;
    if (width >= 1440) {
      setScreenSize('xl');
    } else if (width >= 1024) {
      setScreenSize('lg');
    } else {
      setScreenSize('sm');
    }
  };

  /**
   * 신년사 태그(NEWYEARLG) 포함 되어 있는지 여부
   * @param heroResDto 히어로 컨텐츠
   */
  const isContainNewYearTag = (heroResDto: HeroAreaResDto) => {
    if (heroResDto.tag && heroResDto.tag?.includes('#')) {
      const tagList = heroResDto.tag?.split('#')?.filter(Boolean) || [];
      return tagList.some(tag => tag === 'NEWYEARLG');
    }
    return false;
  };

  /** 신년사 그룹 행사 컨텐츠 여부 */
  const hasIncludedNewYearLgContents = useMemo(() => {
    if (!mainHeroList || !mainHeroList?.length) return false;
    if (mainHeroList?.some(item => item.slotNum === 1 && isContainNewYearTag(item))) return true;
    return PINNED_NEWYEAR_CONTENTS_ID_LIST.some(
      item =>
        item.environmentType === GET_LOADED_ENVIRONMENT_TYPE() &&
        mainHeroList?.some(hero => hero.contentsId === item.mediaId)
    );
  }, [GET_LOADED_ENVIRONMENT_TYPE(), mainHeroList]);

  /**
   * 히어로 [한줄 요약]에 '정지' 라는 단어가 포함되어 있는지 확인
   * - EP 와 달리 자동롤링 기능이 없으므로, 좌/우 버튼 미노출 처리만 진행됨
   */
  const hasStopWordInSummary = useMemo(() => {
    if (!mainHeroList || _.isEmpty(mainHeroList)) return false;
    if (!mainHeroList?.length) return false;
    const firstHero: HeroAreaResDto = mainHeroList?.[0];

    // '정지' 명령어 외에 추가 시간 옵션이 있을 경우
    if (firstHero.summary?.includes('정지,')) {
      const timeFormat = 'YYYY.MM.DD HH:mm';
      const options = firstHero.summary?.substring(firstHero.summary?.indexOf('정지,') + 3, firstHero.summary?.length);
      const timeOptions = !!options.split(',')?.length ? options?.split(',') : [options];

      const isSingleTimeOption = timeOptions?.length === 1;
      const isPeriodTimeOption = timeOptions?.length === 2;

      // 방어 코드1
      if (!timeOptions?.length || (!isSingleTimeOption && !isPeriodTimeOption))
        return !!firstHero.summary?.includes('정지');

      const nowDateTime = moment(new Date(), timeFormat);
      const firstTime = moment(timeOptions[0], timeFormat);

      // 첫번째 시간 값이 유효한 시간 값이 아닌 경우 그냥 '정지' 키워드 존재 유무만 판단
      if (!firstTime.isValid()) return !!firstHero.summary?.includes('정지');

      // 예약 시간 판단 로직(기간)
      if (isPeriodTimeOption) {
        const secondTime = moment(timeOptions[1], timeFormat);

        // 두번째 시간 값이 유효하지 않을 경우, 첫번쨰 시간만 존재하는 걸로 간주하여 판단 -> 첫번째 시간만 존재할 경우 해당 시간을 롤링 시작 시간(종료 무기한)으로 간주
        if (!secondTime.isValid() || !secondTime.isAfter(firstTime)) return firstTime.isBefore(nowDateTime);

        // 두번째 시간이 현재 시간 이후일 경우, 롤링 재개
        if (nowDateTime.isAfter(secondTime)) return false;
        // 현재 시간이 첫번째 시간과 두번째 시간 사이에 해당할 경우 롤링 정지
        return nowDateTime.isAfter(firstTime) && nowDateTime.isBefore(secondTime);
      }

      // 첫번째 시간이 현재 시간 이후일 경우, 롤링 재개 -> 첫번째 시간만 존재할 경우 해당 시간을 롤링 시작 시간(종료 무기한)으로 간주
      // 첫번째 시간이 현재 시간 이전일 경우, 롤링 중지
      return firstTime.isBefore(nowDateTime);
    }

    return !!firstHero.summary?.includes('정지');
  }, [mainHeroList]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const params = {
    slidesPerView: 3,
    spaceBetween: 30,
    centeredSlides: true,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
  };

  if (!mainHeroList) {
    return <></>;
  }

  const messageEventIds = MESSAGE_EVENT_IDS.find(data => data.environmentType === GET_LOADED_ENVIRONMENT_TYPE());

  /**
   * 신년사 태그(NEWYEARLG) 포함 되어 있거나, 하드 코딩된 ID가 있을 경우 좋아요/조회수/댓글수 미노출 여부
   */
  const isHideReadLikeCommentCnt = (heroResDto: HeroAreaResDto) => {
    if (isContainNewYearTag(heroResDto)) return true;
    return hasIncludedNewYearLgContents;
  };

  /**
   * 임원 인사 HOME 고정 배너 HERO ID 여부
   * @param slotNum 노출 배너 번호
   * @param heroId 히어로 ID
   */
  const isExecutiveAppointmentContents = ({ slotNum, heroId }: { slotNum: number; heroId: string }): boolean => {
    if (!heroId) return false;

    const staticHeroBannerList = PINNED_EXECUTIVE_APPOINTMENT_HOME_HERO_ID_LIST.find(
      item => item.slowNum === slotNum
    )?.targetHeroList;

    if (!staticHeroBannerList) return false;

    return staticHeroBannerList
      .filter(targetHero => targetHero.environmentType === GET_LOADED_ENVIRONMENT_TYPE())
      .some(hero => hero.heroId === heroId);
  };

  /**
   * 임원 인사 링크
   */
  const getExecutiveAppointmentLink = () => {
    return String(Paths.getPath('person') + '?yearId=' + executeAppointmentDisplayInfo?.yearId);
  };

  return (
    <>
      <Swiper
        {...params}
        rebuildOnUpdate
        containerClass="hero_swiper"
        centeredSlides={true}
        slidesPerView={'auto'}
        spaceBetween={12}
        speed={900}
        loop={true}
        loopedSlides={3}
        loopAdditionalSlides={1}
        threshold={screenSize !== 'sm' ? 70 : 10}
        navigation={
          !hasStopWordInSummary
            ? {
                nextEl: '.swiper-button-next.is-pc',
                prevEl: '.swiper-button-prev.is-pc',
              }
            : false
        }
        pagination={<div></div>}
        breakpoints={{
          1024: {
            // break point보다 큰 사이즈에 적용 width > 1024
            slidesPerView: 1.148,
            spaceBetween: 12,
          },
          1440: {
            // width > 1440
            slidesPerView: 'auto',
            spaceBetween: 12,
          },
        }}
      >
        {mainHeroList ? (
          mainHeroList.map((hero, idx) => {
            // 75주년 보드 이벤트 임시 덮어쓰기
            if (messageEventIds && messageEventIds.eventIds.find(e => e.eventId === hero.contentsId)) {
              return (
                <SwiperSlide key={`hero_home_${hero.heroId}_${idx}`}>
                  <div key={idx}>
                    <div className="hero_item">
                      <a
                        className="hero_thumb"
                        style={{
                          backgroundImage: `url('${imageUrl(hero.thumbImageUri)}')`,
                        }}
                        {...(hero.heroPostType === 'MEDIA'
                          ? getContentsGAProps({
                              contentsType: hero?.heroPostType as string,
                              title: hero?.title as string,
                              tags: hero?.tag as string,
                              category: 'heroes',
                              dataContentBelong: 'hero 배너',
                              maker: hero?.creatorNm as string,
                              time: hero?.videoTotalTime,
                              brightcoveId: hero?.videoId as string,
                              mediaContentsType: hero?.mediaContentsType as string,
                              dataContentUrl: `${hero.linkUri}`,
                              dataImgUrl: `${hero.thumbImageUri as string}`,
                            })
                          : getEventGAProps({
                              type: hero.eventType as string,
                              url: window.location.hostname + hero.linkUri,
                              title: hero?.title as string,
                              dataContentBelong: 'hero 배너',
                              dataImgUrl: hero.thumbImageUri,
                            }))}
                        href={
                          messageEventIds?.eventIds.find(e => e.eventId === hero.contentsId)?.linkUrl ||
                          Paths.getPath('eventList')
                        }
                        onClick={e => {
                          all_click_datalayer_push(e.currentTarget);
                        }}
                      >
                        <div className="hero_dim is-pc"></div>
                        {hero.heroPostType === 'MEDIA' && hero.mediaContentsType === 'VIDEO' && (
                          <div className="hero_play">
                            <img src="assets/images/icon/ico-action-32-play.svg" alt="play" />
                          </div>
                        )}
                        <div className="hero_sub">
                          <div className="hero_sub_record">
                            <div className="hero_sub_date is-pc">{relativeTime(hero.postDt, props.currentDt)}</div>
                          </div>
                          <div className="hero_page">
                            <span className="is-current">{zeroStringFormat(idx + 1, 2)}</span> / 03
                          </div>
                        </div>
                      </a>
                      <div className="hero_info">
                        <h3 className="hero_title">{hero.title}</h3>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            }
            if (
              !!executeAppointmentDisplayInfo?.yearId &&
              isExecutiveAppointmentContents({ slotNum: hero.slotNum as number, heroId: hero.heroId as string })
            ) {
              return (
                <SwiperSlide key={`hero_home_${hero.heroId}_${idx}`}>
                  <div key={idx}>
                    <div className="hero_item">
                      <a
                        className="hero_thumb"
                        href={getExecutiveAppointmentLink()}
                        style={{
                          // backgroundImage: `url('/assets/images/executive-appointment/2025/home-hero-2025.png')`,
                          backgroundImage: `url('${imageUrl(hero.thumbImageUri)}')`,
                          cursor: 'pointer',
                        }}
                      >
                        <div className="hero_dim is-pc"></div>
                        <div className="hero_sub">
                          <div className="hero_page">
                            <span className="is-current">{zeroStringFormat(idx + 1, 2)}</span> / 03
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
              );
            }
            return (
              <SwiperSlide key={`hero_home_${hero.heroId}_${idx}`}>
                <div key={idx}>
                  <div className="hero_item">
                    <a
                      className="hero_thumb"
                      style={{
                        backgroundImage: `url('${imageUrl(hero.thumbImageUri)}')`,
                      }}
                      {...(hero.heroPostType === 'MEDIA'
                        ? getContentsGAProps({
                            contentsType: hero?.heroPostType as string,
                            title: hero?.title as string,
                            tags: hero?.tag as string,
                            category: 'heroes',
                            dataContentBelong: 'hero 배너',
                            maker: hero?.creatorNm as string,
                            time: hero?.videoTotalTime,
                            brightcoveId: hero?.videoId as string,
                            mediaContentsType: hero?.mediaContentsType as string,
                            dataContentUrl: `${hero.linkUri}`,
                            dataImgUrl: `${hero.thumbImageUri as string}`,
                          })
                        : getEventGAProps({
                            type: hero.eventType as string,
                            url: window.location.hostname + hero.linkUri,
                            title: hero?.title as string,
                            dataContentBelong: 'hero 배너',
                            dataImgUrl: hero.thumbImageUri,
                          }))}
                      href={hero.linkUri || '/'}
                      onClick={e => {
                        all_click_datalayer_push(e.currentTarget);
                      }}
                    >
                      <div className="hero_dim is-pc"></div>
                      {hero.heroPostType === 'MEDIA' && hero.mediaContentsType === 'VIDEO' && (
                        <div className="hero_play">
                          <img src="assets/images/icon/ico-action-32-play.svg" alt="play" />
                        </div>
                      )}
                      <div className="hero_sub">
                        <div className="hero_sub_record">
                          {/* 신년사 조회수/댓글/좋아요 미노출 */}
                          {/* 75주년 이벤트 수정 사항 */}
                          {!isHideReadLikeCommentCnt(hero) && (
                            <>
                              <ul className="card_record">
                                <li>
                                  <img src="assets/images/icon/ico-action-20-view.svg" alt="views" />
                                  {hero.readCnt}
                                </li>
                                <li>
                                  <img src="assets/images/icon/ico-comments.png" alt="comments" />
                                  {hero.commentCnt}
                                </li>
                                <li>
                                  <img src="assets/images/icon/ico-likes.png" alt="likes" />
                                  {hero.likeCnt}
                                </li>
                              </ul>
                            </>
                          )}
                          <div className="hero_sub_date is-pc">{relativeTime(hero.postDt, props.currentDt)}</div>
                        </div>
                        <div className="hero_page">
                          <span className="is-current">{zeroStringFormat(idx + 1, 2)}</span> / 03
                        </div>
                      </div>
                    </a>
                    <div className="hero_info">
                      <h3 className="hero_title">{hero.title}</h3>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })
        ) : (
          <>
            <div></div>
            <div></div>
            <div></div>
          </>
        )}
      </Swiper>
    </>
  );
};

Hero.displayName = 'Hero';

export default React.memo(Hero);
